import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import {} from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils.js';
/**
 * Table — the admin list table, built from divs for row-as-link semantics.
 * Compose: `<Table columns="minmax(0,2.2fr) 160px 120px"><THead><TCell>Name</TCell>…</THead>
 * <TRow href="…"><TCell variant="primary">…</TCell>…</TRow></Table>` + `<TableFooter>`.
 *
 * Cell variants: "primary" (link + sub-line stack), "mono" (IDs), "num"
 * (right-aligned monospace numbers), "stack" (two-line cell — pass
 * `primary` and `sub` instead of children). `state` adds a numeric state:
 * "idle" (muted) or "void" (muted + struck).
 *
 * `TableMeter` is the thin progress bar (value 0-100; `complete` turns it
 * green).
 */
const rowGridClass = 'grid items-center gap-3.5 [grid-template-columns:var(--table-cols,_minmax(0,_2fr)_1fr_1fr)]';
export function Table({ columns = 'minmax(0, 2fr) 1fr 1fr', scrollable = false, children, className, style, ...rest }) {
    return (_jsx("div", { role: "table", style: { ...style, '--table-cols': columns }, className: cn('flex flex-col rounded-card border-[0.5px] border-line bg-surface shadow-card', scrollable ? 'overflow-x-auto [&_[role="row"]]:min-w-max' : 'overflow-hidden', className), ...rest, children: children }));
}
export function THead({ children, className, ...rest }) {
    return (_jsx("div", { role: "row", className: cn(rowGridClass, 'border-b-[0.5px] border-line bg-surface-muted px-5 py-3', '[&_[role="cell"]]:font-ui [&_[role="cell"]]:text-[11px] [&_[role="cell"]]:font-semibold [&_[role="cell"]]:uppercase [&_[role="cell"]]:tracking-[0.06em] [&_[role="cell"]]:text-ink-4', className), ...rest, children: children }));
}
export function TRow({ href, children, className, ...rest }) {
    const rowClass = cn(rowGridClass, 'px-5 py-3.5 text-inherit no-underline transition-colors duration-[120ms] ease-in-out', '[&+&]:border-t-[0.5px] [&+&]:border-line', 'hover:bg-surface-muted', className);
    if (href) {
        return (_jsx("a", { role: "row", href: href, className: rowClass, ...rest, children: children }));
    }
    return (_jsx("div", { role: "row", className: rowClass, ...rest, children: children }));
}
const cellVariants = cva('min-w-0 text-[13.5px] text-ink', {
    variants: {
        variant: {
            primary: 'flex flex-col gap-[3px]',
            mono: 'font-mono text-[12.5px] tracking-[-0.01em] tabular-nums text-ink-3',
            num: 'text-right font-mono text-sm font-semibold tabular-nums',
            stack: 'flex flex-col gap-0.5 [&>*]:text-[13px] [&>*]:text-ink-2 [&>*+*]:text-[12.5px] [&>*+*]:text-ink-4',
        },
        state: {
            idle: 'text-ink-4',
            void: 'text-ink-4 line-through',
        },
    },
});
export function TCell({ variant, state, primary, sub, children, className, ...rest }) {
    return (_jsx("div", { role: "cell", className: cn(cellVariants({ variant, state }), className), ...rest, children: variant === 'stack' ? (_jsxs(_Fragment, { children: [_jsx("span", { children: primary }), _jsx("span", { children: sub })] })) : (children) }));
}
export function TableMeter({ value = 0, complete = false, className, ...rest }) {
    return (_jsx("span", { className: cn('block h-1 w-[72px] overflow-hidden rounded-sm bg-line', className), ...rest, children: _jsx("span", { className: cn('block h-full rounded-[inherit] bg-accent', complete && 'bg-success'), style: { width: `${value}%` } }) }));
}
export function TableFooter({ children, className, ...rest }) {
    return (_jsx("div", { className: cn('flex items-center justify-between gap-4 rounded-card border-[0.5px] border-line bg-surface px-[18px] py-3.5 text-[13px] text-ink-3 shadow-card', className), ...rest, children: children }));
}
//# sourceMappingURL=table.js.map